import { useContext, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { generateSignature } from './prescript';
import { TokenContext } from './TokenContext';

// Utility to get session ID from cookies
const getSessionIdFromCookies = () => {
  const match = document.cookie.match(/(?:^|; )session_id=([^;]*)/);
  return match ? match[1] : '';
};

const useApiWithSession = ({
  url,
  sid1 = '',
  method = 'GET',
  body = null,
  customHeaders = {},
  router = null, // NEW: router prop for navigation
}) => {
  const { tokenKey, tokenSecret } = useContext(TokenContext);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Memoize sessionId to avoid recalculation
  const sessionId = useCallback(getSessionIdFromCookies, []);

  const redirectToErrorPage = useCallback(() => {
    if (router && typeof router.push === 'function') {
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [router]);
  const run = useCallback(async () => {
    try {
      if (!url) {
        setError('Invalid URL');
        dispatch(addErrorToReport(new Error('Invalid URL'), { message: 'useApiWithSession: Invalid URL' }));
        return;
      }
      const activeSessionId = sid1 || sessionId();
      if (!activeSessionId) {
        setError('Session ID missing');
        dispatch(addErrorToReport(new Error('Session ID missing'), { message: 'useApiWithSession: Session ID missing' }));
        return;
      }
      const request = {
        url,
        method,
        data: body ? JSON.stringify(body) : null,
      };
      let hmacToken = '';
      try {
        hmacToken = generateSignature(request, tokenKey, tokenSecret);
      } catch (err) {
        setError('Failed to generate signature');
        dispatch(addErrorToReport(err, { message: 'useApiWithSession: Failed to generate signature' }));
        return;
      }
      const options = {
        method,
        headers: {
          Authorization: hmacToken,
          'Content-Type': 'application/json',
          session_id: activeSessionId,
          ...customHeaders,
        },
        body: request.data,
      };
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const errorText = await response.text();
          const httpError = new Error(`HTTP error! status: ${response.status} - ${errorText}`);
          setError(httpError.message);
          dispatch(addErrorToReport(httpError, { message: 'useApiWithSession: HTTP error' }));
          redirectToErrorPage();
          throw httpError;
        }
        let responseData;
        try {
          responseData = await response.json();
        } catch (jsonErr) {
          setError('Failed to parse response JSON');
          dispatch(addErrorToReport(jsonErr, { message: 'useApiWithSession: Failed to parse response JSON' }));
          redirectToErrorPage();
          throw jsonErr;
        }
        setData(responseData);
      } catch (err) {
        setError(err.message);
        dispatch(addErrorToReport(err, { message: 'useApiWithSession: API call error' }));
        redirectToErrorPage();
      } finally {
        setIsLoading(false);
      }
    } catch (outerErr) {
      setError(outerErr.message);
      dispatch(addErrorToReport(outerErr, { message: 'useApiWithSession: Unexpected error in run' }));
      redirectToErrorPage();
    }
  }, [url, sid1, method,
    body, customHeaders, tokenKey, tokenSecret, sessionId, dispatch, redirectToErrorPage]);

  return {
    isLoading,
    data,
    error,
    run,
  };
};

export default useApiWithSession;
